import { v4 } from "uuid";

import {
  put,
  call,
  take,
  select,
  debug,
  takeEvery,
  createDoRequestAction,
  CreateDoRequestActionOutput,
  SagaIterator,
} from "../../lib";
import {
  SessionOwnershipMark,
  markSessionOwnership,
  onSessionScopeEnd,
  readSharedSessionNonce,
  sessionOwnershipSince,
} from "../../lib/auth/session_scope";
import { sessionDeadlineNow } from "../../lib/auth/session_deadlines";
import {
  AUTH_REFRESHING,
  AUTH_REFRESH_UNAVAILABLE,
  AUTH_REFRESHING_FAILURE,
  AUTH_SET,
  SessionActionType,
  SessionEndReason,
  SessionEndReasonState,
  doSESSION_END_REASON_SET,
} from "../state";
import { doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT } from "./identity_refresh_token_submit";
import { REFRESH_OUTCOME_ACTION_LIST } from "./token_fetcher";

export const REQUEST_IDENTITY_SESSION_REVALIDATE = "REQUEST_IDENTITY_SESSION_REVALIDATE";

export interface RequestIdentitySessionRevalidatePayload {
  // the idle end the caller is checking, on the wall clock, or null when it knows of none
  idleEndsAt: number | null;
  // answer from the refresh coordinator's next attempt rather than asking at once, so a check repeated
  // through an outage keeps to the retry schedule
  waitForRetry?: boolean;
}

export const doREQUEST_IDENTITY_SESSION_REVALIDATE =
  createDoRequestAction<RequestIdentitySessionRevalidatePayload>(
    REQUEST_IDENTITY_SESSION_REVALIDATE,
  );

export const requestIdentitySessionRevalidateActionCreators = {
  doREQUEST_IDENTITY_SESSION_REVALIDATE,
};

// `no_answer`: the refresh did not reach the server, and the coordinator's retry schedule goes on
export type SessionRevalidateOutcome = "success" | "ended" | "no_answer";

export interface SessionRevalidateAnswer {
  outcome: SessionRevalidateOutcome;
}

const leaveForSignIn = () => {
  window.location.href = "/";
};

// An already settled promise resumes the saga only after everything dispatched so far has run, so the
// refresh cycle has finished what follows the outcome it just reported. Until then the cycle is still
// running, and `takeLeading` drops a refresh dispatched onto it without a word.
const settle = (): Promise<void> => Promise.resolve();

const isRefreshing = (state: any): boolean => Boolean(state?.auth?.isRefreshing);
const retryScheduled = (state: any): boolean => Boolean(state?.auth?.refreshUnavailable);

function* sendTime(): SagaIterator<number> {
  const own = yield select((state: any) => state?.auth?.sessionDeadlines ?? null);
  return sessionDeadlineNow(own);
}

interface RevalidateCheck {
  idleEndsAt: number | null;
  // taken when the check was asked for
  ownership: SessionOwnershipMark;
  // the browser sign-in the check speaks for: the tab's own, or the browser's for a tab that holds none
  sessionNonce: string | null | undefined;
  signOutBegan: () => boolean;
}

// The refresh has already ended the session: the token is gone and the scope with it. When the check
// still speaks for this tab, the tab ends locally. It sends no logout and no idle announcement, since the
// other tabs hear it from the released sign-in, and the full navigation leaves nothing of the session in
// memory. Otherwise the refresh's handling stands: a sign-in in this tab since, a sign-out this tab began,
// or another sign-in on record now.
function* endLocally(
  check: RevalidateCheck,
  sentAt: number | null,
  reason: SessionEndReason | null,
): SagaIterator {
  if (check.signOutBegan() || sessionOwnershipSince(check.ownership) === "signedIn") return;
  const sharedNonce = readSharedSessionNonce();
  if (sharedNonce && sharedNonce !== check.sessionNonce) return;

  let endReason: SessionEndReasonState | null = yield select(
    (state: any) => state?.session?.endReason ?? null,
  );
  // only a request sent once the idle end had passed shows that idleness ended the session
  const idleEndHadPassed =
    check.idleEndsAt !== null && sentAt !== null && sentAt >= check.idleEndsAt;
  if (idleEndHadPassed && (reason === null || reason === SessionEndReason.SessionEnded)) {
    endReason = { reason: SessionEndReason.Inactivity, at: Date.now() };
  }
  // The refresh drops only the token from the session record, and the sign-in page boots from what is
  // left: the signed-out user's profile, identity and settings. The end reason persists into the same
  // record, so it goes back in after.
  window.sessionStorage.removeItem("labfront");
  if (endReason) yield put(doSESSION_END_REASON_SET(endReason));
  yield call(leaveForSignIn);
}

// Asks the server whether the session is still going, before an idle warning or an idle sign-out. It asks
// with a refresh, never with an activity report, which would itself keep the session going. Like the
// activity report it answers its caller directly, exactly once, and never through REQUEST_COMPLETE or
// REQUEST_ERROR: no request-state entry, no loading indicator, no outage notice, no replay.
//
// The answer comes from a refresh sent after the check was asked for. One already on the wire was sent
// earlier, and `takeLeading` would drop a new dispatch onto it, so that one is heard out first and the
// server is then asked again. If it ends the session that is the answer: nothing a later refresh says can
// bring the session back, and it could only act for whoever holds the cookie by then.
//
// `waitForRetry` is for a check that already went unanswered: the coordinator retries on its own, and a
// dispatch would send its next attempt at once. That attempt answers instead, or the one already out,
// whose send time is not known, so it cannot show the idle end had passed. With no cycle running there is
// no schedule to keep to, and the check asks.
export function* requestIdentitySessionRevalidate({
  payload,
  setRequestResult,
}: CreateDoRequestActionOutput<RequestIdentitySessionRevalidatePayload>): SagaIterator {
  debug("saga*requestIdentitySessionRevalidate");
  const answer = (outcome: SessionRevalidateOutcome) => setRequestResult?.({ outcome } as any);

  const ownNonce: string | null = yield select((state: any) => state?.auth?.sessionNonce ?? null);
  let signOutBegan = false;
  const stopListening = onSessionScopeEnd((_, { signingOut }) => {
    if (signingOut) signOutBegan = true;
  });
  const check: RevalidateCheck = {
    idleEndsAt: payload?.idleEndsAt ?? null,
    ownership: markSessionOwnership(),
    sessionNonce: ownNonce ?? readSharedSessionNonce(),
    signOutBegan: () => signOutBegan,
  };

  try {
    let sentAt: number | null = null;
    if (payload?.waitForRetry) {
      if (!(yield select(isRefreshing)) && !(yield select(retryScheduled))) {
        yield put(doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT({ requestId: v4() }));
      }
    } else {
      if (yield select(isRefreshing)) {
        const { type } = yield take(REFRESH_OUTCOME_ACTION_LIST);
        if (type === AUTH_REFRESHING_FAILURE) {
          // sent before the check, so it cannot show the idle end had passed when it went out
          yield call(endLocally, check, null, null);
          return answer("ended");
        }
      }

      yield call(settle);
      // the session ended in this tab meanwhile, a sibling's sign-out say, so there is nothing left to ask about
      if (sessionOwnershipSince(check.ownership) === "ended") return answer("ended");

      // the cycle may already have begun an attempt of its own after the one it reported
      if (yield select(isRefreshing)) sentAt = yield call(sendTime);
      else yield put(doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT({ requestId: v4() }));
    }

    // the reason the refresh gives the end, if any, arrives just before its outcome
    let reason: SessionEndReason | null = null;
    for (;;) {
      const action = yield take([
        AUTH_REFRESHING,
        SessionActionType.SESSION_END_REASON_SET,
        ...REFRESH_OUTCOME_ACTION_LIST,
      ]);
      if (action.type === AUTH_REFRESHING) {
        sentAt = yield call(sendTime);
      } else if (action.type === SessionActionType.SESSION_END_REASON_SET) {
        reason = action.payload?.reason ?? null;
      } else if (action.type === AUTH_SET) {
        return answer("success");
      } else if (action.type === AUTH_REFRESH_UNAVAILABLE) {
        return answer("no_answer");
      } else {
        yield call(endLocally, check, sentAt, reason);
        return answer("ended");
      }
    }
  } finally {
    stopListening();
  }
}

export function* identitySessionRevalidateSaga() {
  yield takeEvery(REQUEST_IDENTITY_SESSION_REVALIDATE, requestIdentitySessionRevalidate);
}
